import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useDocuments } from '@/hooks/useDocuments';
import { supabase } from '@/integrations/supabase/client';
import { FileText, Loader2, Upload, X, ChevronLeft } from 'lucide-react';
import { SpecificationDocumentCard } from './SpecificationDocumentCard';
import { mapProductCategoryToJobCategory } from '@/lib/utils/categoryMapping';

interface UploadSpecificationFormProps {
  productCategoryId: string;
  productCategoryName: string;
  onSave: (specData: any) => void;
  onBack: () => void;
  onCancel: () => void;
  existingSpecification?: any;
  isEditMode?: boolean;
}

export function UploadSpecificationForm({
  productCategoryId,
  productCategoryName,
  onSave,
  onBack,
  onCancel,
  existingSpecification,
  isEditMode = false
}: UploadSpecificationFormProps) {
  const { toast } = useToast();
  const [specificationName, setSpecificationName] = useState(
    existingSpecification?.specification_name || ''
  );
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState(
    existingSpecification?.content?.subcategory_id || ''
  );
  const [description, setDescription] = useState(
    existingSpecification?.specification_description || ''
  );
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [documentId, setDocumentId] = useState<string | undefined>(
    existingSpecification?.specification_document_id
  );

  // Generate a temporary entity ID for document upload
  const [entityId] = useState(() => crypto.randomUUID());
  const [subcategoryOptions, setSubcategoryOptions] = useState<string[]>([]);
  const [loadingSubcategories, setLoadingSubcategories] = useState(true);
  
  const { uploadDocument, validateFile } = useDocuments({
    entityType: 'specification',
    entityId
  });

  // Fetch the correct subcategory options from specification_field_definitions
  useEffect(() => {
    const fetchSubcategoryOptions = async () => {
      if (!productCategoryId) return;
      
      setLoadingSubcategories(true);
      try {
        // Step 1: Find the "*Subcategory" specification category for this product category
        const { data: subcategoryCategory } = await supabase
          .from('specification_categories')
          .select('id, name')
          .eq('product_category_id', productCategoryId)
          .eq('is_active', true)
          .ilike('name', '%Subcategory')
          .maybeSingle();

        if (!subcategoryCategory) {
          setSubcategoryOptions([]);
          setLoadingSubcategories(false);
          return;
        }

        // Step 2: Get the first field definition in this category to get allowed values
        const { data: fieldDef } = await supabase
          .from('specification_field_definitions')
          .select('id, field_name, field_type, validation_rules')
          .eq('category_id', subcategoryCategory.id)
          .eq('is_active', true)
          .order('display_order', { ascending: true })
          .limit(1)
          .maybeSingle();

        if (!fieldDef) {
          setSubcategoryOptions([]);
          setLoadingSubcategories(false);
          return;
        }

        // Step 3: Extract allowed_values from validation_rules
        const validationRules = fieldDef.validation_rules as Record<string, unknown> | null;
        const allowedValues = (validationRules?.allowed_values || validationRules?.allowedValues || []) as string[];
        
        setSubcategoryOptions(allowedValues);
      } catch (error) {
        console.error('Error fetching subcategory options:', error);
        setSubcategoryOptions([]);
      } finally {
        setLoadingSubcategories(false);
      }
    };

    fetchSubcategoryOptions();
  }, [productCategoryId]);

  useEffect(() => {
    if (existingSpecification?.content) {
      setSelectedSubcategoryId(existingSpecification.content.subcategory_id || '');
    }
  }, [existingSpecification]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      toast({
        title: 'Invalid file',
        description: validationError,
        variant: 'destructive',
      });
      return;
    }

    try {
      setUploading(true);
      const uploadedDoc = await uploadDocument(file);
      if (uploadedDoc) {
        setDocumentId(uploadedDoc.id);
        setUploadedFile(file);
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      console.error('Full error details:', JSON.stringify(err, null, 2));
      toast({
        title: 'Upload Failed',
        description: err instanceof Error ? err.message : 'Failed to upload document',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setDocumentId(undefined);
  };

  const handleSave = async () => {
    // Validation
    if (!specificationName.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Specification name is required',
        variant: 'destructive',
      });
      return;
    }

    if (!selectedSubcategoryId) {
      toast({
        title: 'Validation Error',
        description: 'Please select a subcategory',
        variant: 'destructive',
      });
      return;
    }

    if (!isEditMode && !documentId) {
      toast({
        title: 'Validation Error',
        description: 'Please upload a specification document',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      const specData = {
        specification_name: specificationName.trim(),
        specification_description: description.trim(),
        category: mapProductCategoryToJobCategory(productCategoryName),
        specification_type: 'personal',
        specification_document_id: documentId,
        content: {
          input_method: 'upload',
          product_category_id: productCategoryId,
          product_category_name: productCategoryName,
          subcategory_id: selectedSubcategoryId,
          subcategory_name: selectedSubcategoryId, // Now stores the actual subcategory name
          document_metadata: uploadedFile ? {
            original_filename: uploadedFile.name,
            file_size: uploadedFile.size,
            mime_type: uploadedFile.type,
            uploaded_at: new Date().toISOString()
          } : existingSpecification?.content?.document_metadata
        },
        sections: [],
        required_fields: [],
        tags: ['upload', productCategoryName.toLowerCase()],
      };

      await onSave(specData);
    } catch (error) {
      console.error('Error saving specification:', error);
      toast({
        title: 'Error',
        description: 'Failed to save specification',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Upload Specification Document</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {productCategoryName} • Upload your specification file
          </p>
        </div>
        {!isEditMode && (
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
        )}
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="spec-name">
            Specification Name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="spec-name"
            value={specificationName}
            onChange={(e) => setSpecificationName(e.target.value)}
            placeholder={`e.g., ${productCategoryName} Technical Specifications`}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="subcategory">
            Subcategory <span className="text-destructive">*</span>
          </Label>
          <Select
            value={selectedSubcategoryId}
            onValueChange={setSelectedSubcategoryId}
            disabled={loadingSubcategories}
          >
            <SelectTrigger id="subcategory">
              <SelectValue placeholder={loadingSubcategories ? "Loading..." : "Select subcategory"} />
            </SelectTrigger>
            <SelectContent>
              {subcategoryOptions.map(option => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!isEditMode && (
          <div className="space-y-2">
            <Label>
              Specification Document <span className="text-destructive">*</span>
            </Label>
            {!documentId ? (
              <Card>
                <CardContent className="p-6">
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <div className="flex flex-col items-center text-center">
                      <div className="p-4 rounded-full bg-primary/10 text-primary mb-3">
                        {uploading ? (
                          <Loader2 className="h-8 w-8 animate-spin" />
                        ) : (
                          <Upload className="h-8 w-8" />
                        )}
                      </div>
                      <p className="font-medium mb-1">
                        {uploading ? 'Uploading...' : 'Click to upload specification document'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        PDF, DOCX, XLSX, PNG, JPG, CSV • Max 200MB
                      </p>
                    </div>
                  </label>
                  <input
                    id="file-upload"
                    type="file"
                    className="hidden"
                    accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg,.csv"
                    onChange={handleFileSelect}
                    disabled={uploading}
                  />
                </CardContent>
              </Card>
            ) : (
              <div className="relative">
                <SpecificationDocumentCard
                  documentId={documentId}
                  showDownloadButton={false}
                  showMetadata={true}
                  compact={true}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={handleRemoveFile}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}

        {isEditMode && documentId && (
          <div className="space-y-2">
            <Label>Current Document</Label>
            <SpecificationDocumentCard
              documentId={documentId}
              showDownloadButton={true}
              showMetadata={true}
              compact={false}
            />
            <p className="text-xs text-muted-foreground">
              Document cannot be replaced when editing. Create a new specification to use a different document.
            </p>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="description">Description (Optional)</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add any additional notes or context about this specification..."
            rows={3}
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {isEditMode ? 'Updating...' : 'Saving...'}
            </>
          ) : (
            isEditMode ? 'Update Specification' : 'Save Specification'
          )}
        </Button>
      </div>
    </div>
  );
}